'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';

import { enableTrial } from '@/features/subscription/subscriptionSlice';
import { FREE_TRIAL_DAYS } from '@/config/consts';
import { AppDispatch, RootState } from '@/store';

const features = [
  {
    title: 'AI-Powered Responses',
    description:
      'Generate accurate, ready-to-file notice responses in minutes — not hours.',
    icon: (
      <svg
        className="w-6 h-6 text-primary-700"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z"
        />
      </svg>
    ),
  },
  {
    title: 'Smart Notice Management',
    description:
      'Upload, track, and manage all your tax notices and demands from one dashboard.',
    icon: (
      <svg
        className="w-6 h-6 text-primary-700"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 00-1.883 2.542l.857 6a2.25 2.25 0 002.227 1.932H19.05a2.25 2.25 0 002.227-1.932l.857-6a2.25 2.25 0 00-1.883-2.542m-16.5 0V6A2.25 2.25 0 016 3.75h3.879a1.5 1.5 0 011.06.44l2.122 2.12a1.5 1.5 0 001.06.44H18A2.25 2.25 0 0120.25 9v.776"
        />
      </svg>
    ),
  },
  {
    title: 'Analytics & Insights',
    description:
      'Get real-time visibility into demands, deadlines, and compliance status across all clients.',
    icon: (
      <svg
        className="w-6 h-6 text-primary-700"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={1.5}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
        />
      </svg>
    ),
  },
];

const steps = [
  {
    step: '1',
    title: 'Start your free trial',
    description: 'No credit card needed. Get instant access to all features.',
  },
  {
    step: '2',
    title: 'Add your clients',
    description: 'Import or add client details to organize your practice.',
  },
  {
    step: '3',
    title: 'Upload & respond',
    description: 'Drop in tax notices — AI handles the rest.',
  },
];

type WelcomeSectionProps = {
  userName: string;
};

export default function WelcomeSection({ userName }: WelcomeSectionProps) {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const subscriptionState = useSelector((state: RootState) => state.subscription);
  const requestAction = useSelector((state: RootState) => state.auth.requestAction);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (requestAction === 'FREE_TRIAL') {
      setLoading(true);
      handleEnableTrial();
    }
  }, []);

  const handleEnableTrial = async () => {
    try {
      await dispatch(enableTrial()).unwrap();
      router.push('/notice-dashboard');
    } catch (error) {
      console.error('Failed to enable trial:', error);
    } finally {
      setLoading(false);
    }
  };

  const isActivating = subscriptionState.loading || loading;
  const displayName = userName.charAt(0).toUpperCase() + userName.slice(1);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16">
      {/* Hero Section */}
      <div className="text-center mb-10 md:mb-14">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
          Welcome, {displayName}!
        </h2>
        <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto mb-8">
          Start your {FREE_TRIAL_DAYS}-day free trial to experience AI-powered
          notice management — no credit card required.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={handleEnableTrial}
            disabled={isActivating}
            className="px-6 py-3 text-sm md:text-base font-semibold text-white bg-primary-700 hover:bg-primary-600 rounded-standard transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isActivating ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Activating trial...
              </span>
            ) : (
              `Start ${FREE_TRIAL_DAYS}-day free trial`
            )}
          </button>
          <Link
            href="/onboarding"
            className="px-6 py-3 text-sm md:text-base font-semibold text-gray-700 hover:text-gray-900 border border-gray-300 hover:border-gray-400 rounded-standard text-center transition-colors"
          >
            Explore plans
          </Link>
        </div>
      </div>

      {/* What you'll unlock */}
      <div className="mb-10 md:mb-14">
        <h3 className="text-lg md:text-xl font-semibold text-gray-900 text-center mb-6 md:mb-8">
          What you&apos;ll get with your trial
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {features.map((feature) => (
            <div
              key={feature.title}
              className="bg-white border border-gray-200 rounded-xl p-5 md:p-6 hover:border-primary-300 hover:shadow-sm transition-all"
            >
              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center mb-4">
                {feature.icon}
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-1.5">
                {feature.title}
              </h4>
              <p className="text-sm text-gray-500 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* How it works */}
      <div className="mb-10 md:mb-14">
        <h3 className="text-lg md:text-xl font-semibold text-gray-900 text-center mb-6 md:mb-8">
          Get started in 3 simple steps
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          {steps.map((item) => (
            <div key={item.step} className="flex gap-4 items-start p-4 md:p-5">
              <div className="w-8 h-8 bg-primary-700 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                {item.step}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1">
                  {item.title}
                </h4>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trust indicators */}
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-gray-400">
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
          </svg>
          No credit card required
        </span>
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Full access to all features
        </span>
        <span className="flex items-center gap-1.5">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Cancel anytime
        </span>
      </div>
    </div>
  );
}
